import clsx from 'clsx';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import Countdown from '../../../components/Countdown';
import Footnote from '../../../components/Footnote';
import GptBanner from '../../../components/GptBanner';
import Loader from '../../../components/Loader';
import Modal from '../../../components/Modal';
import PollCarousel from '../../../components/PollCarousel';
import PreviousPolls from '../../../components/PreviousPolls';
import PrizePanel from '../../../components/PrizePanel';
import UpperBar from '../../../components/UpperBar';
import { useOptions } from '../../../hooks/useOptions';
import { loadPoll } from '../../../models/polls';
import { loadSponsorData } from '../../../models/sponsors';
import { getPollStatuses } from '../../../resources/pollStatuses';
import { useAppDispatch, useAppSelector } from '../../../store';
import type { PollType, SponsorType } from '../../../types';
import { appEnv } from '../../../utils/environment';
import GoogleTagManagerHelper from '../../../utils/googleTagManager';
import { getPageSubType } from '../../../utils/utils';

import * as style from './style.module.scss';

export const sendTracking = (
  competitionBanner: string,
  currentPoll: PollType | null,
  appType: string,
  seasonYear: string,
  sponsor: SponsorType | null,
  directLink: boolean | undefined,
  isLoaded: boolean,
  language: string,
) => {
  if (!currentPoll || !isLoaded) return;
  const competitionFolder = location.pathname.split('/').find((x) => x !== '') ?? competitionBanner;

  const pollTitleEn = currentPoll?.translations?.title?.EN ?? '';

  const trackingData = {
    competition: window.competitionTracking ?? competitionBanner,
    competitionDate: seasonYear,
    pageName: `/${competitionFolder}/${appType}/`,
    partners: sponsor?.code ?? '',
    contentSection1: window.competitionTracking ?? competitionBanner,
    contentSection2: appType,
    pageType: 'hub',
    pageSubType: getPageSubType(currentPoll, directLink),
    environment: appEnv,
  };
  GoogleTagManagerHelper.addPageMeta(trackingData, language);
  if (directLink) {
    GoogleTagManagerHelper.addVirtualPage({
      virtPath: `/${competitionFolder}/${appType}/past-winners/${pollTitleEn.replace(/[ ,.]/gim, '-').toLowerCase()}/`,
    });
  }
};

const MainGOTx = () => {
  const params: { pollId?: string } = useParams();
  const { pollId } = params;
  const directLink = !!pollId;

  const {
    competitionBanner,
    advPosition,
    advSlotType,
    advZone,
    promoTag,
    appType,
    cup,
    seasonYear,
    pollType,
    pollSubtype,
    enableHeader,
    sponsorPhase,
  } = useOptions();
  const {
    i18n: { language },
  } = useTranslation();

  const dispatch = useAppDispatch();

  const currentPoll = useAppSelector((state) => state.polls.currentPoll);
  const isLoading = useAppSelector((state) => state.polls.isLoading);
  const sponsor = useAppSelector((state) => state.sponsors.sponsor);
  const isLoaded = useAppSelector((state) => state.sponsors.isLoaded);

  useEffect(() => {
    sendTracking(competitionBanner, currentPoll, appType, seasonYear, sponsor, directLink, isLoaded, language);
  }, [currentPoll, appType, directLink, competitionBanner, sponsor, isLoaded, seasonYear, language]);

  useEffect(() => {
    dispatch(loadPoll({ cup, seasonYear, pollType, pollSubtype, appType, pollId }));
  }, [appType, cup, dispatch, pollId, pollSubtype, pollType, seasonYear]);

  const { pollIsAvailable, pollIsScheduled, pollIsOpen } = useMemo(() => getPollStatuses(currentPoll), [currentPoll]);

  useEffect(() => {
    dispatch(loadSponsorData({ cup, mainDirectory: appType, sponsorPhase }));
  }, [appType, cup, sponsorPhase, dispatch]);

  const showPreviousPoll = useMemo(() => !pollId || pollId === currentPoll?.id, [pollId, currentPoll?.id]);
  const mainContent = (
    <div data-testid='main-gotx' className={clsx('gotx-App', style.gotxApp, `${appType}-App`)}>
      {enableHeader && !directLink && <UpperBar mainHeader />}
      <div className={style.gotxContainerFluid}>
        {isLoading && <Loader />}
        {currentPoll && (
          <>
            {pollIsScheduled && (
              <div data-testid='countdown' className={style.gotxCountdown}>
                <Countdown
                  datetime={currentPoll?.openingDateTime}
                  heading='countdown_intro_text_nominees'
                  backgroundColor='background'
                />
              </div>
            )}
            {pollIsAvailable && (
              <>
                <div className={style.gotxCarousel}>
                  <PollCarousel poll={currentPoll} />
                </div>
                {pollIsOpen && !!promoTag && <PrizePanel poll={currentPoll} />}
                {!pollIsScheduled && !!competitionBanner && !!advZone && (
                  <GptBanner
                    adUnitPath={`/5874/${competitionBanner}.{language}/${advZone}`}
                    position={advPosition ?? 'middle'}
                    slotType={advSlotType ?? 'leaderboard'}
                  />
                )}
              </>
            )}
            {showPreviousPoll && <PreviousPolls excludedPoll={currentPoll?.id} />}
          </>
        )}
        {pollIsScheduled && !!competitionBanner && !!advZone && (
          <GptBanner
            adUnitPath={`/5874/${competitionBanner}.{language}/${advZone}`}
            position={advPosition ?? 'middle'}
            slotType={advSlotType ?? 'leaderboard'}
          />
        )}
      </div>
      <Footnote />
    </div>
  );

  return directLink ? (
    <Modal showModal className={'gotx_modal_content'} overlayClassName={'gotx_modal'}>
      <UpperBar />
      {mainContent}
    </Modal>
  ) : (
    mainContent
  );
};

export default MainGOTx;
